import { roundMoney } from "@/modules/pos/lib/pricing";
import { isStaffMealName } from "@/modules/pos/lib/staffMeal";

/**
 * THE OFF-PREMISE PAPER'S RULES (roundtable 2026-10-06, T17, T19, T20) — pure,
 * so the device artifact (`coordinatorArtifact.tsx`), the browser receipt
 * (`Receipt.tsx`) and the browser chit (`KitchenChit.tsx`) print the SAME
 * figures from the same arithmetic, and a spec can pin each rule once.
 */

/**
 * The base a VAT rate was applied to (T20), from the bill's own snapshot —
 * `applyInvoiceCharges`' arithmetic read backwards, so "VAT 14% on 130.00"
 * states the figure the till actually multiplied:
 *
 * · exclusive pricing: the subtotal (net of any discount), plus the service
 *   charge when VAT is charged on it (D35);
 * · inclusive pricing: the VAT-EXCLUSIVE amount — total less service charge
 *   less VAT (the "net before charges" line) — plus, again, the service
 *   charge when VAT rides on it. The customer's 100 contains the tax, so the
 *   base is what remains once it is taken out, never the 100 itself.
 */
export function vatLineBase(bill: {
  subtotal: number;
  serviceChargeTotal: number;
  vatTotal: number;
  total: number;
  vatOnServiceCharge: boolean;
  pricesIncludeTax: boolean;
}): number {
  const onService = bill.vatOnServiceCharge ? bill.serviceChargeTotal : 0;
  if (bill.pricesIncludeTax) {
    return roundMoney(
      bill.total - bill.serviceChargeTotal - bill.vatTotal + onService,
    );
  }
  return roundMoney(bill.subtotal + onService);
}

/**
 * A FIRST NAME ONLY on the kitchen chit (T19): a cook calls "Ahmed" across the
 * pass, not a full name, and a chit left lying there should carry no more of a
 * customer than that. `null` for a blank name.
 */
export function firstName(name: string | null | undefined): string | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0] ?? null;
}

/**
 * THE NAME A KITCHEN CHIT PRINTS (T19, D172): the first name, except that the
 * staff-meal door's bill name («وجبة الموظفين», legacy «وجبة طاقم») prints
 * WHOLE — «وجبة» alone tells the grill nothing, and the whole name is what
 * stops the crew's meal being bagged for a rider.
 */
export function kitchenFirstName(name: string | null | undefined): string | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  return isStaffMealName(trimmed) ? trimmed : firstName(trimmed);
}

/**
 * The first line of an address (T17) — up to the first line break or comma.
 * The rider block is read at the door; the full address is on the lines above.
 */
export function firstAddressLine(address: string | null | undefined): string | null {
  const trimmed = (address ?? "").trim();
  if (!trimmed) return null;
  return trimmed.split(/[\n,،]/)[0]?.trim() || null;
}

/**
 * WHAT THE RIDER COLLECTS AT THE DOOR (T17).
 *
 * `collect` with the amount still owed — total less what has been paid toward
 * the bill — printed large and LTR so a minus can never flip and a rider
 * reading in a doorway cannot misread it. `nothing` on a PLATFORM bill (the
 * platform took the customer's money; a rider collecting again is the
 * double-charge this line exists to prevent) and on a bill already paid in
 * full. `paidTotal` is the ledger's tendered magnitude, 0 when nothing.
 */
export function riderCollect(bill: {
  onPlatform: boolean;
  total: number;
  paidTotal: number;
}): { kind: "collect"; amount: number } | { kind: "nothing" } {
  if (bill.onPlatform) return { kind: "nothing" };
  const owed = roundMoney(bill.total - bill.paidTotal);
  return owed > 0.004 ? { kind: "collect", amount: owed } : { kind: "nothing" };
}
